// Store removed - config injected via params
import { ExtendedChartWidgetProps } from '../types';
import {
	translateQueryFromJSON,
	widgetComposer,
	type DataSchemaContext,
} from '@sisense/sdk-ui/analytics-composer';
import { isChartWidgetProps } from '@sisense/sdk-ui';
import { HttpClient } from '@sisense/sdk-rest-client';
import { nanoidHex } from '@/utils/id-utils';
import { devDebug } from '@/utils/dev-debug';
import {
	getNlqResultLight,
	type QueryExecutionResult,
	type QueryState,
} from '@/services/nlq/nlq-service';
import { buildQueryEngine } from '../../build-query/build-query-engine';
import { QueryResult } from '../../build-query/types';

/** Internal synthetic ids for chart-inline prior NLQ; never part of LLM-facing schemas. */
export const INLINE_PRIOR_NLQ_ID_PREFIX = '__inline_prior_nlq__:';

/**
 * Minimal {@link QueryResult} for {@link buildQueryEngine}'s `retrieveQuery` —
 * only `nlqResult.queryState` is read when resolving `priorQueryId`.
 */
export function minimalQueryResultShimFromQueryState(
	queryState: QueryState,
	syntheticQueryId: string,
): QueryResult {
	return {
		queryId: syntheticQueryId,
		title: queryState.title ?? '',
		nlqResult: {
			queryState,
			dataSchema: {} as DataSchemaContext,
			timestamp: '',
		} as QueryExecutionResult,
	};
}

export type GenerateChartTableResult = {
	widgetProps: ExtendedChartWidgetProps;
};

/**
 * Build table-phase chart widget props from an NLQ execution result (translate query JSON → columns).
 */
export function tableWidgetPropsFromNlqExecution(
	nlqResult: QueryExecutionResult,
	chartTitle: string,
): ExtendedChartWidgetProps {
	const dataSchema = nlqResult.dataSchema;
	if (!dataSchema) {
		throw new Error('Error: Data schema not found');
	}

	const translationResult = translateQueryFromJSON({
		data: nlqResult.queryState,
		context: dataSchema,
	});
	if (!translationResult.success) {
		throw new Error(
			'Error: Failed to translate query JSON: ' +
				JSON.stringify(translationResult.errors),
		);
	}

	const translatedParams = translationResult.data;

	const columns = [
		...(translatedParams.dimensions || []),
		...(translatedParams.measures || []),
	];

	return {
		title: chartTitle,
		id: nanoidHex(),
		widgetType: 'chart',
		dataSource: translatedParams.dataSource,
		chartType: 'table',
		dataOptions: { columns },
		filters: translatedParams.filters || [],
		highlights: translatedParams.highlights || [],
		queryState: nlqResult.queryState,
		...(nlqResult.dataset !== undefined && { dataset: nlqResult.dataset }),
	};
}

/**
 * Load table-phase widget props from a stored build-query result (verbatim reuse; no NLQ round-trip).
 */
export async function loadChartTableFromQueryId(
	queryId: string,
	retrieveQuery: (queryId: string) => QueryResult | null,
	fallbackTitle: string,
): Promise<GenerateChartTableResult> {
	const queryResult = retrieveQuery(queryId);
	if (!queryResult) {
		throw new Error('Error: Failed to get query result.');
	}
	const nlqResult = queryResult.nlqResult;
	if (!nlqResult) {
		throw new Error('Error: Failed to get query result.');
	}
	const chartTitle = queryResult.title || fallbackTitle;
	const widgetProps = tableWidgetPropsFromNlqExecution(nlqResult, chartTitle);
	return { widgetProps };
}

const generateChartTableFromNlqV2 = async (
	dataSourceTitle: string,
	queryPrompt: string,
	httpClient?: HttpClient,
): Promise<ExtendedChartWidgetProps> => {
	const nlqResult = await getNlqResultLight(
		dataSourceTitle,
		{
			text: queryPrompt,
			timezone: 'UTC',
		},
		httpClient,
	);

	if (!nlqResult) {
		throw new Error(
			'Error: Failed to get NLQ result. Ask the user to specify the data source again.',
		);
	}

	const widgetProps = widgetComposer.toWidgetProps(
		{ ...nlqResult, chartRecommendations: {} },
		{
			useCustomizedStyleOptions: true,
		},
	) as ExtendedChartWidgetProps;

	if (!widgetProps) {
		throw new Error('Error: Failed to generate widget props');
	}

	if (!isChartWidgetProps(widgetProps)) {
		throw new Error('Error: Non-chart widget props not supported yet');
	}

	// set widget title if it's not set
	widgetProps.title = widgetProps.title || nlqResult.nlqPrompt || queryPrompt;

	return widgetProps;
};

const generateChartTableFromNlqV3 = async (
	dataSourceTitle: string,
	queryPrompt: string,
	httpClient?: HttpClient,
	retrieveQuery?: (queryId: string) => QueryResult | null,
	nlqCommonFilters?: unknown,
	priorQueryState?: QueryState,
	isNlqDeltaEnabled?: boolean,
): Promise<GenerateChartTableResult> => {
	try {
		const deltaEnabled = isNlqDeltaEnabled !== false;
		let scopedRetrieve: ((id: string) => QueryResult | null) | undefined;
		let priorQueryId: string | undefined;
		if (priorQueryState !== undefined) {
			priorQueryId = `${INLINE_PRIOR_NLQ_ID_PREFIX}${nanoidHex()}`;
			const shim = minimalQueryResultShimFromQueryState(
				priorQueryState,
				priorQueryId,
			);
			scopedRetrieve = (id: string) =>
				id === priorQueryId ? shim : retrieveQuery?.(id) ?? null;
		}
		const queryResult = await buildQueryEngine(
			{
				dataSourceTitle,
				queryPrompt,
				...(priorQueryId !== undefined && { priorQueryId }),
			},
			{
				toolCallId: 'build-chart-internal',
				httpClient,
				...(nlqCommonFilters !== undefined && {
					commonFilters: nlqCommonFilters,
				}),
				...(scopedRetrieve !== undefined && {
					retrieveQuery: scopedRetrieve,
				}),
				isNlqDeltaEnabled: deltaEnabled,
			},
		);
		const nlqResult = queryResult.nlqResult;
		if (!nlqResult) {
			throw new Error('Error: Failed to get NLQ result.');
		}
		const chartTitle = queryResult.title || queryPrompt;
		const widgetProps = tableWidgetPropsFromNlqExecution(
			nlqResult,
			chartTitle,
		);
		return { widgetProps };
	} catch (error) {
		if (priorQueryState !== undefined) {
			throw error;
		}
		console.error(
			'Error: Failed to generate chart table from NLQ V3. Falling back to NLQ V2.',
			error,
		);
		const widgetProps = await generateChartTableFromNlqV2(
			dataSourceTitle,
			queryPrompt,
			httpClient,
		);
		return { widgetProps };
	}
};

/**
 * Run NLQ / build-query and materialize a table-phase chart widget (no verbatim `queryId` path).
 * For verbatim reuse of a stored query, use {@link loadChartTableFromQueryId} from the caller.
 */
export const generateChartTable = async (
	dataSourceTitle: string,
	queryPrompt: string,
	isNlqV3Enabled?: boolean,
	httpClient?: HttpClient,
	nlqCommonFilters?: unknown,
	priorQueryState?: QueryState,
	isNlqDeltaEnabled?: boolean,
	retrieveQuery?: (queryId: string) => QueryResult | null,
): Promise<GenerateChartTableResult> => {
	if (isNlqV3Enabled) {
		devDebug('toolCall', {
			tool: 'generateChartTable',
			nlqVersion: 'v3',
			queryPrompt,
			dataSourceTitle,
		});
		return generateChartTableFromNlqV3(
			dataSourceTitle,
			queryPrompt,
			httpClient,
			retrieveQuery,
			nlqCommonFilters,
			priorQueryState,
			isNlqDeltaEnabled,
		);
	} else {
		devDebug('toolCall', {
			tool: 'generateChartTable',
			nlqVersion: 'v2',
			queryPrompt,
			dataSourceTitle,
		});
		const widgetProps = await generateChartTableFromNlqV2(
			dataSourceTitle,
			queryPrompt,
			httpClient,
		);
		return { widgetProps };
	}
};
